import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* Self-contained HSV colour picker — a hue WHEEL (spectrum disk) + a
 * saturation/value square + a hex field. NO dependency, NO native
 * <input type="color"> (which renders as a poor default swatch set on mobile).
 * Works with pointer events (mouse + touch) and the keyboard (arrow keys on the
 * hue ring and the SV square). Emits a #RRGGBB hex on every change; the parent
 * keeps hex validation + the existing social.brand.* save flow. */

const HEX = /^#[0-9a-fA-F]{6}$/;

interface HSV { h: number; s: number; v: number }

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!HEX.test(hex)) return null;
  return { r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16) };
}

function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function rgbToHsv(r: number, g: number, b: number): HSV {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

function hsvToRgb({ h, s, v }: HSV): { r: number; g: number; b: number } {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rp = 0, gp = 0, bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return { r: (rp + m) * 255, g: (gp + m) * 255, b: (bp + m) * 255 };
}

function hsvToHex(hsv: HSV): string {
  const { r, g, b } = hsvToRgb(hsv);
  return rgbToHex(r, g, b);
}

export function ColorWheelPicker({
  value,
  onChange,
  label,
  fallback = '#A5622E',
}: {
  value: string;
  onChange: (hex: string) => void;
  label: string;
  fallback?: string;
}) {
  const seed = HEX.test(value) ? value : fallback;
  const [hsv, setHsv] = useState<HSV>(() => {
    const rgb = hexToRgb(seed)!;
    return rgbToHsv(rgb.r, rgb.g, rgb.b);
  });
  const [hexText, setHexText] = useState(value);
  const ringRef = useRef<HTMLDivElement>(null);
  const svRef = useRef<HTMLDivElement>(null);

  // Sync when the parent changes the value externally (e.g. reset / load).
  useEffect(() => {
    setHexText(value);
    if (HEX.test(value)) {
      const rgb = hexToRgb(value)!;
      const next = rgbToHsv(rgb.r, rgb.g, rgb.b);
      // Avoid clobbering hue while the user drags a greyscale value (s or v = 0).
      setHsv((prev) => (hsvToHex(prev) === value.toLowerCase() || hsvToHex(prev) === value ? prev : next));
    }
  }, [value]);

  const emit = useCallback((next: HSV) => {
    setHsv(next);
    const hex = hsvToHex(next);
    setHexText(hex);
    onChange(hex);
  }, [onChange]);

  const hueColor = useMemo(() => hsvToHex({ h: hsv.h, s: 1, v: 1 }), [hsv.h]);

  // ---- hue ring (disk) pointer + keyboard ----
  const setHueFromPoint = useCallback((clientX: number, clientY: number) => {
    const el = ringRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const deg = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
    const h = (deg + 90 + 360) % 360; // 0deg at top, clockwise
    emit({ ...hsv, h });
  }, [emit, hsv]);

  const dragRing = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setHueFromPoint(e.clientX, e.clientY);
  }, [setHueFromPoint]);

  const moveRing = useCallback((e: React.PointerEvent) => {
    if (e.buttons !== 1) return;
    setHueFromPoint(e.clientX, e.clientY);
  }, [setHueFromPoint]);

  const keyRing = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); emit({ ...hsv, h: (hsv.h + step) % 360 }); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); emit({ ...hsv, h: (hsv.h - step + 360) % 360 }); }
  }, [emit, hsv]);

  // ---- SV square pointer + keyboard ----
  const setSvFromPoint = useCallback((clientX: number, clientY: number) => {
    const el = svRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const s = clamp((clientX - r.left) / r.width, 0, 1);
    const v = clamp(1 - (clientY - r.top) / r.height, 0, 1);
    emit({ ...hsv, s, v });
  }, [emit, hsv]);

  const dragSv = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setSvFromPoint(e.clientX, e.clientY);
  }, [setSvFromPoint]);

  const moveSv = useCallback((e: React.PointerEvent) => {
    if (e.buttons !== 1) return;
    setSvFromPoint(e.clientX, e.clientY);
  }, [setSvFromPoint]);

  const keySv = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    let { s, v } = hsv;
    if (e.key === 'ArrowRight') s = clamp(s + step, 0, 1);
    else if (e.key === 'ArrowLeft') s = clamp(s - step, 0, 1);
    else if (e.key === 'ArrowUp') v = clamp(v + step, 0, 1);
    else if (e.key === 'ArrowDown') v = clamp(v - step, 0, 1);
    else return;
    e.preventDefault();
    emit({ ...hsv, s, v });
  }, [emit, hsv]);

  const onHexInput = (raw: string) => {
    const t = raw.startsWith('#') ? raw : `#${raw}`;
    setHexText(t);
    if (HEX.test(t)) {
      const rgb = hexToRgb(t)!;
      emit(rgbToHsv(rgb.r, rgb.g, rgb.b));
    }
  };

  // knob geometry on the hue ring
  const ringSize = 132;
  const knobR = ringSize / 2 - 10;
  const rad = ((hsv.h - 90) * Math.PI) / 180;
  const knobX = ringSize / 2 + knobR * Math.cos(rad);
  const knobY = ringSize / 2 + knobR * Math.sin(rad);

  const inputCls =
    'h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  return (
    <div className="space-y-3">
      <span className="block text-xs font-medium text-text-secondary">{label}</span>
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        {/* Hue wheel (spectrum disk) */}
        <div
          ref={ringRef}
          role="slider"
          tabIndex={0}
          aria-label={`${label}: hue`}
          aria-valuemin={0}
          aria-valuemax={360}
          aria-valuenow={Math.round(hsv.h)}
          onPointerDown={dragRing}
          onPointerMove={moveRing}
          onKeyDown={keyRing}
          className="relative shrink-0 touch-none rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          style={{
            width: ringSize,
            height: ringSize,
            background:
              'conic-gradient(from 0deg, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%))',
            cursor: 'pointer',
          }}
        >
          <span
            className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
            style={{ left: knobX, top: knobY, background: hueColor }}
            aria-hidden
          />
        </div>

        {/* Saturation / Value square */}
        <div
          ref={svRef}
          role="slider"
          tabIndex={0}
          aria-label={`${label}: saturation and brightness`}
          aria-valuetext={`saturation ${Math.round(hsv.s * 100)}%, brightness ${Math.round(hsv.v * 100)}%`}
          aria-valuenow={Math.round(hsv.v * 100)}
          onPointerDown={dragSv}
          onPointerMove={moveSv}
          onKeyDown={keySv}
          className="relative h-[132px] w-[132px] shrink-0 touch-none rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          style={{
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`,
            cursor: 'crosshair',
          }}
        >
          <span
            className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
            style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hsvToHex(hsv) }}
            aria-hidden
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span
          className="h-control w-10 shrink-0 rounded-md border border-border-default"
          style={{ background: HEX.test(hexText) ? hexText : hsvToHex(hsv) }}
          aria-hidden
        />
        <input
          className={inputCls}
          value={hexText}
          onChange={(e) => onHexInput(e.target.value)}
          placeholder={fallback}
          spellCheck={false}
          aria-label={`${label} hex value`}
          maxLength={7}
        />
      </div>
    </div>
  );
}
